// ============================================================
//  src/pages/inbox/InboxPickerPage.js
//
//  Landing screen for the sidebar's "Inbox" nav item. Inbox is a
//  per-bot resource, so before we can show one we need to know
//  *which* bot. With exactly one bot there's nothing to choose —
//  skip straight to it. With multiple bots, show a picker grid.
// ============================================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { listBots } from '../../api/client';
import './InboxPages.css';

const SIDEBAR_WIDTH = 260; // must match --sidebar-width in src/index.css
const TOPBAR_HEIGHT  = 58;  // must match .topbar { height: 58px } in AppLayout.css
const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || 'B';
}

export default function InboxPickerPage() {
  const navigate = useNavigate();
  const [bots, setBots]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const isMobile = useIsMobile();

  // Pinned to the viewport for the same reason InboxBotPage is — see
  // the comment there. Keeps this page immune to the fragile flex-height
  // cascade through AppLayout, and gives it its own guaranteed-bounded
  // scroll container instead of relying on the page growing/shrinking.
  const fixedShellStyle = {
    position: 'fixed',
    top: isMobile ? TOPBAR_HEIGHT : 0,
    left: isMobile ? 0 : SIDEBAR_WIDTH,
    right: 0,
    bottom: 0,
    overflowY: 'auto',
    boxSizing: 'border-box',
    background: '#f5f7fb',
    zIndex: 5,
  };

  useEffect(() => {
    let cancelled = false;
    listBots()
      .then(res => {
        if (cancelled) return;
        const list = res.data?.bots ?? [];
        setBots(list);
        // Exactly one bot — nothing to actually pick, go straight there.
        if (list.length === 1) {
          navigate(`/inbox/${list[0].bId}`, { replace: true });
          return;
        }
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { toast.error('Failed to load your bots'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [navigate]);

  const filtered = search.trim()
    ? bots.filter(b => (b.bName || '').toLowerCase().includes(search.trim().toLowerCase()))
    : bots;

  if (loading) {
    return (
      <div style={fixedShellStyle}>
        <div className="inbox-picker-loading">
          <span className="spinner spinner-dark" /> Loading your bots…
        </div>
      </div>
    );
  }

  if (bots.length === 0) {
    return (
      <div style={fixedShellStyle}>
        <div className="inbox-picker-empty">
          <div className="inbox-picker-empty-icon">💬</div>
          <h2>No bots yet</h2>
          <p>Create a WhatsApp bot first, then its Inbox will show up here.</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={fixedShellStyle}>
      <div className="inbox-picker" style={{ padding: 32, boxSizing: 'border-box' }}>
        <div className="inbox-picker-header">
          <h1>Inbox</h1>
          <p>Pick a bot to view its WhatsApp conversations.</p>
        </div>

        {bots.length > 5 && (
          <div className="search-bar inbox-picker-search">
            <span className="search-icon">🔍</span>
            <input
              className="search-input"
              placeholder="Search bots…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        )}

        <div className="inbox-picker-grid">
          {filtered.map(bot => (
            <button
              key={bot.bId}
              type="button"
              className="inbox-picker-card"
              onClick={() => navigate(`/inbox/${bot.bId}`)}
            >
              <div className="inbox-picker-avatar">{initials(bot.bName)}</div>
              <div className="inbox-picker-card-meta">
                <div className="inbox-picker-card-name">{bot.bName}</div>
                <div className="inbox-picker-card-sub">
                  <span className={`status-dot ${bot.bIsActive == 1 ? 'active' : 'inactive'}`} />
                  {bot.bIsActive == 1 ? 'Active' : 'Inactive'}
                </div>
              </div>
              <span className="inbox-picker-card-arrow">→</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="inbox-picker-no-match">No bots match "{search}"</div>
          )}
        </div>
      </div>
    </div>
  );
}